import { MetadataIcon } from "@/types";

import { cn } from "@/lib/utils";
import React, { forwardRef } from "react";
import { getIconBackgroundColorClass } from "../IconSelector";
import { defaultProjectIcon } from "../ResourcePill";

const LucideIcon = React.lazy(() => import("./LucideIcon"));

export interface IconProps {
  name?: string;
  color?: string;
  size?: number | string;
}

export const Icon = ({
  icon,
  size = 32,
}: {
  icon?: MetadataIcon | null;
  size?: number;
}) => {
  if (!icon) return null;

  switch (icon.type) {
    case "emoji":
      return (
        <span
          style={{
            fontSize: size,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: size,
            height: size,
          }}
        >
          {icon.emoji}
        </span>
      );
    case "custom": {
      if (icon.custom?.url) {
        try {
          const { href } = new URL(
            icon.custom.url,
            import.meta.env.VITE_STATIC_BASE_URL,
          );

          return (
            <img src={href} alt="Icon" style={{ width: size, height: size }} />
          );
        } catch (e) {}
      }

      return null;
    }
    case "lucide":
      return (
        <React.Suspense>
          <LucideIcon
            size={size}
            color={icon.lucide?.color || "currentColor"}
            name={icon.lucide?.name}
          />
        </React.Suspense>
      );
    default:
      return null;
  }
};

export const IconWithBackground = forwardRef<
  HTMLDivElement,
  {
    icon?: MetadataIcon | null;
    size?: number;
  } & React.HTMLAttributes<HTMLDivElement>
>(({ icon = defaultProjectIcon, size = 40, ...props }, ref) => {
  const backgroundColorClass = getIconBackgroundColorClass(icon);

  const iconSize = icon?.custom
    ? size
    : icon?.emoji
      ? Math.floor(size * 0.65)
      : Math.floor(size * 0.55);

  return (
    <div
      ref={ref}
      {...props}
      className={cn(
        {
          [`${backgroundColorClass}`]: backgroundColorClass,
          [`shrink-0 w-[${size}px] h-[${size}px] flex items-center justify-center`]:
            true,
          "overflow-hidden": !backgroundColorClass,
          "rounded-full": icon?.type !== "emoji",
        },
        props.className,
      )}
    >
      <Icon icon={icon} size={iconSize} />
    </div>
  );
});

IconWithBackground.displayName = "IconWithBackground";
